<template>
  <div class="container-fluid py-4">
     <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento in corso...</div>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-body">

        <!-- TITOLO + AZIONI -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0 fw-semibold">Andamento Risposta Quesiti</h5>

          <div class="d-flex align-items-center gap-2">

            <!-- Dropdown esperti (solo gestore) -->
            <select v-if="isGestore" v-model="selectedUserId" @change="fetchStats"
              class="form-select form-select-sm w-auto">
              <option v-for="e in experts" :key="e.id" :value="e.id">
                {{ e.surname }} {{ e.name }}
              </option>
            </select>

            <!-- Anno o periodo personalizzato -->
            <select v-model="selectedPeriodType" @change="onPeriodTypeChange" class="form-select form-select-sm w-auto">
              <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              <option value="custom">Periodo Personalizzato</option>
            </select>

            <!-- Se periodo personalizzato → mostra input date -->
            <div v-if="selectedPeriodType === 'custom'" class="d-flex gap-1">
              <input type="date" class="form-control" v-model="dateFrom" />
              <input type="date" class="form-control" v-model="dateTo" @change="fetchStats" />
            </div>

            <!-- Export -->
            <div class="dropdown">
              <button class="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                <i class="bi bi-download me-1"></i> Esporta
              </button>

              <ul class="dropdown-menu dropdown-menu-end">
                <li><a class="dropdown-item" @click="exportPNG">PNG</a></li>
                <li><a class="dropdown-item" @click="exportCSV">CSV</a></li>
                <li><a class="dropdown-item" @click="exportExcelWithChart">XLSX</a></li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Riga elegante + utente -->
        <div class="d-flex justify-content-between align-items-center border-top pt-2 mb-3">
          <div class="fw-semibold">
            <i class="bi bi-person-circle me-1"></i>
            {{ selectedUserName }}
          </div>
        </div>

        <!-- KPI principali -->
        <div class="d-flex gap-4 mb-3">
          <div class="kpi-label text-warning">
            <span class="kpi-value">{{ stats.assigned }}</span> Quesiti assegnati
          </div>
          <div class="kpi-label text-success">
            <span class="kpi-value">{{ stats.answered }}</span> Quesiti evasi
          </div>
        </div>

        <!-- Grafico -->
        <VChart ref="chartRef" :option="chartOptions" style="height: 350px; width: 100%" />

      </div>
    </div>

    <!-- Storico -->
    <div class="card shadow-sm">
      <div class="card-body">
        <h5 class="fw-semibold mb-4">Storico Quesiti</h5>
        <div class="row text-center">
          <div class="col" v-for="(value, key) in kpis" :key="key">
            <div :class="['kpi-box', value.color]">
              <div class="kpi-number">{{ value.value }}</div>
              <div class="kpi-text">{{ value.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

export default {
  name: 'DashboardView',

  data() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 4; i++) {
      years.push(currentYear - i);
    }
    return {
      chartRef: null,
      echartsInstance: null,

      assegnati: [],
      evasi: [],
      labels: [],

      stats: {
        assigned: 0,
        inProgress: 0,
        answered: 0,
        totalAssigned: 0,
        totalInProgress: 0,
        totalAnswered: 0,
        assevered: 0,
        published: 0,
        notPublished: 0,
        nonAssevered: 0
      },

      year: new Date().getFullYear(),
      //years: [2023, 2024, 2025, 2026],
      years,
      loggedUser: { fullName: '' },

      isGestore: false,
      experts: [],
      selectedUserId: null,

      dateFrom: null,
      dateTo: null,
      selectedPeriodType: new Date().getFullYear(),
      isLoading: true,
    }
  },

  computed: {
    selectedUserName() {
      if (!this.isGestore || !this.selectedUserId) return this.loggedUser.fullName;
      const user = this.experts.find(u => u.id === this.selectedUserId);
      return user ? `${user.surname} ${user.name}` : this.loggedUser.fullName;
    },
    chartOptions() {
      return {
        tooltip: { trigger: 'axis' },
        legend: { data: ['Assegnati', 'Evasi'] },
        xAxis: {
          type: 'category',
          data: this.labels
        },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'Assegnati',
            type: 'line',
            smooth: true,
            data: this.assegnati,
            itemStyle: { color: '#ffc107' },
            lineStyle: { color: '#ffc107', width: 3 }
          },
          {
            name: 'Evasi',
            type: 'line',
            smooth: true,
            data: this.evasi,
            itemStyle: { color: '#198754' },
            lineStyle: { color: '#198754', width: 3 }
          }
        ]
      }
    },

    kpis() {
      return {
        assigned: {
          label: 'Quesiti Assegnati',
          value: this.stats.totalAssigned,
          color: 'text-warning'
        },
        inProgress: {
          label: 'Quesiti in Lavorazione',
          value: this.stats.totalInProgress,
          color: 'text-warning'
        },
        answered: {
          label: 'Quesiti Evasi',
          value: this.stats.totalAnswered,
          color: 'text-success'
        },
        assevered: {
          label: 'Quesiti Asseverati',
          value: this.stats.assevered,
          color: 'text-success'
        },
        published: {
          label: 'Quesiti Pubblicati',
          value: this.stats.published,
          color: 'text-success'
        },
        nonAssevered: {
          label: 'Quesiti Non Asseverati',
          value: this.stats.nonAssevered,
          color: 'text-danger'
        }
      }
    }
  },

  watch: {
    dateFrom() {
      //this.fetchStats()
    },
    dateTo() {
      this.fetchStats()
    }
  },

  mounted() {
    this.echartsInstance = this.$refs.chartRef.chart
    if (this.isGestore && !this.selectedUserId) {
      this.selectedUserId = null
    }
    this.fetchStats()
  },

  methods: {
    onPeriodTypeChange() {
      if (this.selectedPeriodType !== 'custom') {
        this.dateFrom = null
        this.dateTo = null
        this.year = this.selectedPeriodType
        this.fetchStats()
      }
    },
    async fetchStats() {
      this.isLoading = true;
      try {
      const params = {
        userId: this.selectedUserId
      }

      // se ho selezionato periodo custom, mando le date, altrimenti fallback all'anno
      if (this.selectedPeriodType === 'custom' && this.dateFrom && this.dateTo) {
        params.dateFrom = this.dateFrom
        params.dateTo = this.dateTo
      } else {
        params.year = this.year
      }

      const res = await this.$root.$api.get('/dashboard/stats', { params })
      const data = res.data

      this.loggedUser.fullName = data.user.fullName
      this.isGestore = data.isGestore
      this.experts = data.experts ?? []

      // se sono gestore e non ho ancora un utente selezionato, lo imposto e rilancio fetch
      if (this.isGestore && !this.selectedUserId) {
        this.selectedUserId = data.user.id
        return this.fetchStats() 
      }

      // aggiorno i dati della dashboard
      this.stats = {
        assigned: data.assigned,
        inProgress: data.inProgress,
        answered: data.answered,
        totalAssigned: data.totalAssigned,
        totalInProgress: data.totalInProgress,
        totalAnswered: data.totalAnswered,
        assevered: data.totalAssevered,
        published: data.totalPublished,
        notPublished: data.totalNotPublished,
        nonAssevered: data.totalNonAssevered
      }

      this.assegnati = data.assignedPerMonth
      this.evasi = data.answeredPerMonth
      this.labels = data.labels
      }

     catch (err) {
        console.error('Errore fetchStats:', err);
      }
      finally {
        this.isLoading = false;
      }
    },

    exportPNG() {
      const url = this.echartsInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })

      const link = document.createElement('a')
      link.href = url
      link.download = 'andamento_quesiti.png'
      link.click()
    },

    exportCSV() {
      let csv = 'ANDAMENTO RISPOSTA QUESITI\nPeriodo;Quesiti Assegnati;Quesiti Evasi\n'

      this.labels.forEach((l, i) => {
        csv += `${l};${this.assegnati[i] ?? 0};${this.evasi[i] ?? 0}\n`
      })

      csv += '\nSTORICO MIEI QUESITI\nTipo;Valore\n'
      for (let k in this.kpis) {
        csv += `${this.kpis[k].label};${this.kpis[k].value}\n`
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      saveAs(blob, 'dashboard_quesiti.csv')
    },

    async exportExcelWithChart() {
      const chartDataUrl = this.echartsInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })

      const base64 = chartDataUrl.split(',')[1]
      const buffer = Uint8Array.from(atob(base64), c => c.charCodeAt(0))

      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet('Dashboard')

      ws.addRow(['ANDAMENTO RISPOSTA QUESITI'])
      ws.addRow(['Periodo', 'Quesiti Assegnati', 'Quesiti Evasi'])

      this.labels.forEach((l, i) => {
        ws.addRow([l, this.assegnati[i] ?? 0, this.evasi[i] ?? 0])
      })

      ws.addRow([])
      ws.addRow(['STORICO MIEI QUESITI'])
      ws.addRow(['Tipo', 'Valore'])

      for (let k in this.kpis) {
        ws.addRow([this.kpis[k].label, this.kpis[k].value])
      }

      const imageId = wb.addImage({ buffer, extension: 'png' })
      ws.addImage(imageId, {
        tl: { col: 4, row: 1 },
        ext: { width: 1200, height: 240 }
      })

      const wbout = await wb.xlsx.writeBuffer()
      saveAs(new Blob([wbout]), 'dashboard_quesiti.xlsx')
    }
  }
}
</script>
<style scoped>
.kpi-label {
  font-size: 0.95rem;
}

.kpi-value {
  font-weight: 700;
  font-size: 1.2rem;
  margin-right: 4px;
}

.kpi-box {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kpi-number {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1;
}

.kpi-text {
  font-size: 0.85rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.loading-text {
  margin-top: 1rem;
  font-weight: bold;
  color: #0d6efd;
}
</style>
